import React from 'react';
import { Car, BarChart2, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров автокредита
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating
}) => {
  // Расчет текущей стоимости автомобиля
  const carPrice = parseFloat(formData.carPrice) || 0;
  
  // Расчет текущего первоначального взноса
  const downPayment = parseFloat(formData.downPayment) || 0;
  const downPaymentPercent = (downPayment / carPrice) * 100 || 0;
  
  // Расчет текущего срока в месяцах
  const loanTermMonths = Math.round((parseFloat(formData.loanTerm) || 0) * 12);
  
  // Обработчик изменения стоимости автомобиля
  const handleCarPriceChange = (e) => {
    const newValue = e.target.value;
    const numValue = parseFloat(newValue) || 0;
    
    // Сохраняем процентное соотношение первоначального взноса
    const currentPercent = downPayment / carPrice || 0.2; // По умолчанию 20%
    const newDownPayment = (numValue * currentPercent).toFixed(0);
    
    setFormData(prev => ({
      ...prev,
      carPrice: newValue,
      downPayment: newDownPayment,
      downPaymentPercent: (currentPercent * 100).toFixed(2)
    }));
    
    // Очищаем ошибку если она есть
    if (errors.carPrice) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.carPrice;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения первоначального взноса
  const handleDownPaymentChange = (e) => {
    const newValue = e.target.value;
    const numValue = parseFloat(newValue) || 0;
    const percentValue = ((numValue / carPrice) * 100).toFixed(2);
    
    setFormData(prev => ({
      ...prev,
      downPayment: newValue,
      downPaymentPercent: percentValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.downPayment) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.downPayment;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения процента первоначального взноса
  const handleDownPaymentPercentChange = (e) => {
    const newPercent = e.target.value;
    const numPercent = parseFloat(newPercent) || 0;
    const newDownPayment = ((carPrice * numPercent) / 100).toFixed(0);
    
    setFormData(prev => ({
      ...prev,
      downPayment: newDownPayment,
      downPaymentPercent: newPercent
    }));
    
    // Очищаем ошибку если она есть
    if (errors.downPaymentPercent) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.downPaymentPercent;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения срока кредита
  const handleLoanTermChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      loanTerm: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.loanTerm) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.loanTerm;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения процентной ставки
  const handleInterestRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      interestRate: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.interestRate) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.interestRate;
        return newErrors;
      });
    }
  };
  
  // Предопределенные стоимости автомобилей для быстрого выбора
  const predefinedCarPrices = [
    { value: '500000', label: '500 тыс.' },
    { value: '1000000', label: '1 млн' },
    { value: '1500000', label: '1.5 млн' },
    { value: '2000000', label: '2 млн' }
  ];
  
  // Предопределенные проценты первоначального взноса для быстрого выбора
  const predefinedDownPaymentPercents = [
    { value: '10', label: '10%' },
    { value: '20', label: '20%' },
    { value: '30', label: '30%' },
    { value: '50', label: '50%' }
  ];
  
  // Предопределенные сроки кредита для быстрого выбора
  const predefinedTerms = [
    { value: '1', label: '1 год' },
    { value: '3', label: '3 года' },
    { value: '5', label: '5 лет' },
    { value: '7', label: '7 лет' }
  ];
  
  // Функция расчета
  const calculateLoan = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Стоимость автомобиля */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="carPriceSlider" className="text-gray-600 dark:text-gray-400">Стоимость автомобиля</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(carPrice, 0)}</span>
          </div>
          <input
            type="range"
            id="carPriceSlider"
            min="100000"
            max="5000000"
            step="50000"
            value={carPrice}
            onChange={handleCarPriceChange}
            aria-label="Стоимость автомобиля"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными стоимостями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedCarPrices.map(price => (
              <button
                key={price.value}
                type="button"
                onClick={() => {
                  const percentValue = downPaymentPercent / 100 || 0.2; // По умолчанию 20%
                  const newDownPayment = (parseFloat(price.value) * percentValue).toFixed(0);
                  
                  setFormData(prev => ({ 
                    ...prev, 
                    carPrice: price.value,
                    downPayment: newDownPayment
                  }));
                }}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.carPrice === price.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {price.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Первоначальный взнос */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="downPaymentPercentSlider" className="text-gray-600 dark:text-gray-400">Первоначальный взнос</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatCurrency(downPayment, 0)} ({downPaymentPercent.toFixed(1)}%)
            </span>
          </div>
          <input
            type="range"
            id="downPaymentPercentSlider"
            min="0"
            max="80"
            step="5"
            value={downPaymentPercent}
            onChange={handleDownPaymentPercentChange}
            aria-label="Процент первоначального взноса"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными процентами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedDownPaymentPercents.map(percent => (
              <button
                key={percent.value}
                type="button"
                onClick={() => {
                  const newDownPayment = ((carPrice * parseFloat(percent.value)) / 100).toFixed(0);
                  setFormData(prev => ({ 
                    ...prev, 
                    downPayment: newDownPayment,
                    downPaymentPercent: percent.value
                  }));
                }}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.downPaymentPercent === percent.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {percent.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Срок кредита */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="loanTermSlider" className="text-gray-600 dark:text-gray-400">Срок кредита</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.loanTerm} {pluralize(parseFloat(formData.loanTerm) || 0, 'год', 'года', 'лет')} ({loanTermMonths} мес.)
            </span>
          </div>
          <input
            type="range"
            id="loanTermSlider"
            min="1"
            max="10"
            step="0.5"
            value={formData.loanTerm}
            onChange={handleLoanTermChange}
            aria-label="Срок кредита (лет)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными сроками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedTerms.map(term => (
              <button
                key={term.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, loanTerm: term.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.loanTerm === term.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {term.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Процентная ставка */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="interestRateSlider" className="text-gray-600 dark:text-gray-400">Процентная ставка</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.interestRate}%</span>
          </div>
          <input
            type="range"
            id="interestRateSlider"
            min="1"
            max="25"
            step="0.1"
            value={formData.interestRate}
            onChange={handleInterestRateChange}
            aria-label="Процентная ставка (%)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные процентные ставки */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['7.5', '9.5', '12.0', '15.0'].map(rate => (
              <button
                key={rate}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, interestRate: rate }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.interestRate === rate 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {rate}%
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор типа автомобиля */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип автомобиля</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ 
                ...prev, 
                carType: 'new',
                interestRate: '9.5',
                carAge: '0'
              }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.carType === 'new' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium flex items-center justify-center">
                <Car className="w-4 h-4 mr-1.5" />
                Новый
              </div>
              <div className="text-xs opacity-75">∼9.5% ставка</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({
                ...prev,
                carType: 'used',
                interestRate: '12.5',
                carAge: prev.carAge === '0' ? '3' : prev.carAge
              }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.carType === 'used' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium flex items-center justify-center">
                <Car className="w-4 h-4 mr-1.5" />
                Подержанный
              </div>
              <div className="text-xs opacity-75">∼12.5% ставка</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateLoan}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать автокредит
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default QuickAdjustmentSidebar;